import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import api from '../lib/api';
import DetailSearchSort, { filterAndSortRows } from './DetailSearchSort';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const SIZE_ORDER = ['S','M','L','XL','2XL','3XL','4XL','5XL','6XL','7XL','8XL','9XL','Free Size','32','34','36','38','40','42'];
const fmtDate = (d) => { if (!d) return ''; const p = d.split('-'); return p.length === 3 && p[0].length === 4 ? `${p[2]}-${p[1]}-${p[0].slice(-2)}` : d; };

const PurchaseEntryDetail = ({ user }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pe, setPe] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [searchDesign, setSearchDesign] = useState('');
  const [sortBy, setSortBy] = useState('');

  const canManage = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';

  const loadData = useCallback(async () => {
    try {
      const [peRes, vendRes, prodRes, brandRes, whRes] = await Promise.all([
        api.get(`/purchase-entries/${id}`), api.get('/vendors'), api.get('/products'), api.get('/brands'), api.get('/warehouses'),
      ]);
      setPe(peRes.data); setVendors(vendRes.data); setProducts(prodRes.data); setBrands(brandRes.data); setWarehouses(whRes.data);
    } catch { toast.error('Failed to load PE'); navigate('/purchase-entry'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const getVendorName = (vid) => { const v = vendors.find(x => x.id === vid); if (!v) return 'Unknown'; const loc = v.city?.toLowerCase() === 'mumbai' ? v.area : v.city; return loc ? `${v.name} (${loc})` : v.name; };
  const getWarehouseName = (wid) => warehouses.find(w => w.id === wid)?.name || '';
  const getProductImages = (pid) => { const p = products.find(x => x.id === pid); return p?.images || []; };
  const getBrandByProduct = (pid) => { const p = products.find(x => x.id === pid); return brands.find(b => b.id === p?.brand_id)?.name || ''; };

  const totalQty = pe?.total_qty || (pe?.items || []).reduce((s, i) => s + (i.variants || []).reduce((vs, v) => vs + (v.qty_received || 0), 0), 0);
  const totalCost = pe?.total_cost || 0;

  const deletePE = async () => {
    if (!window.confirm('Delete this Purchase Entry? Stock will be reversed.')) return;
    try { await api.delete(`/purchase-entries/${pe.id}`); toast.success('PE deleted & stock reversed'); navigate('/purchase-entry'); }
    catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div></div>;
  if (!pe) return null;

  // Build pivot
  const allItems = pe.items || [];
  const usedSizes = new Set();
  allItems.forEach(item => (item.variants || []).forEach(v => usedSizes.add(v.size)));
  const activeSizes = SIZE_ORDER.filter(s => usedSizes.has(s));

  let sr = 0, grandQty = 0, grandAmount = 0;
  const rows = [];
  allItems.forEach((item) => {
    const colorMap = {}, colorOrder = [];
    (item.variants || []).forEach(v => {
      const c = v.color || 'Other';
      if (!colorMap[c]) { colorMap[c] = { sizes: {}, qty: 0, costs: {}, wsps: {} }; colorOrder.push(c); }
      colorMap[c].sizes[v.size] = (colorMap[c].sizes[v.size] || 0) + (v.qty_received || 0);
      colorMap[c].qty += (v.qty_received || 0);
      colorMap[c].costs[v.size] = v.cost || 0;
      colorMap[c].wsps[v.size] = v.wsp_local || v.wsp_all || 0;
    });
    colorOrder.sort((a, b) => (a || '').localeCompare(b || '')).forEach(color => {
      sr++;
      const d = colorMap[color];
      let amount = 0;
      activeSizes.forEach(s => { amount += (d.sizes[s] || 0) * (d.costs[s] || 0); });
      grandQty += d.qty; grandAmount += amount;
      const costGroups = [];
      let currentGroup = null;
      activeSizes.forEach(s => {
        if ((d.sizes[s] || 0) === 0) return;
        const cost = d.costs[s] || 0;
        if (currentGroup && currentGroup.cost === cost) { currentGroup.sizes.push(s); }
        else { currentGroup = { cost, sizes: [s] }; costGroups.push(currentGroup); }
      });
      rows.push({ sr, item, color, sizes: d.sizes, costs: d.costs, wsps: d.wsps, qty: d.qty, amount, costGroups });
    });
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6 animate-fadeIn" data-testid="pe-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="pe-detail-back" className="text-gray-500 hover:text-gray-900">
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>Back
          </Button>
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900 font-mono" data-testid="pe-detail-number">{pe.grn_number}</h1>
              <Badge className="bg-green-100 text-green-700 text-sm" data-testid="pe-detail-status">Received</Badge>
            </div>
            <h2 className="text-xl font-bold text-blue-800 mt-1" data-testid="pe-detail-vendor">{getVendorName(pe.vendor_id)}</h2>
            <div className="flex items-center gap-3 flex-wrap mt-1 text-sm text-gray-500">
              <span>{fmtDate(pe.entry_date)}</span>
              {pe.invoice_number && <span>Invoice: {pe.invoice_number}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="text-green-600 border-green-300" onClick={async () => {
            try {
              const res = await api.get(`/purchase-entries/${pe.id}/barcode-excel`, { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
              const a = document.createElement('a'); a.href = url; a.download = `barcodes-${pe.grn_number}.xlsx`; a.click();
              window.URL.revokeObjectURL(url);
              toast.success('Barcode Excel downloaded!');
            } catch { toast.error('Failed to download'); }
          }} data-testid="pe-barcode-excel">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>Barcode Excel
          </Button>
          {canManage && (
            <>
              <Button size="sm" variant="outline" className="text-blue-600 border-blue-300" onClick={() => navigate('/create-purchase-entry', { state: { editPE: pe } })} data-testid="pe-detail-edit">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>Edit
              </Button>
              <Button size="sm" variant="outline" className="text-red-600 border-red-300" onClick={deletePE} data-testid="pe-detail-delete">
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>Delete
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="pe-detail-summary">
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Total Cost</p><p className="text-xl font-bold text-gray-900">₹{totalCost.toLocaleString('en-IN')}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Total Qty</p><p className="text-xl font-bold text-blue-700">{totalQty} pcs</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Items</p><p className="text-xl font-bold text-emerald-700">{allItems.length}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Warehouse</p><p className="text-lg font-bold text-gray-700">{getWarehouseName(pe.warehouse_id) || '-'}</p></CardContent></Card>
        <Card><CardContent className="p-4 text-center"><p className="text-xs text-gray-500 mb-1">Linked PO</p><p className="text-lg font-bold text-indigo-600 cursor-pointer hover:underline" onClick={() => { const linked = allItems.find(i => i.linked_po_id); if (linked) navigate(`/purchase-order/${linked.linked_po_id}`); }}>{allItems.find(i => i.linked_po_id) ? 'View PO' : 'None'}</p></CardContent></Card>
      </div>

      {/* Info */}
      <div className="flex gap-6 flex-wrap text-sm text-gray-600 bg-gray-50 p-3 rounded-lg">
        {pe.invoice_number && <span>Invoice: <strong>{pe.invoice_number}</strong></span>}
        {pe.challan_number && <span>Challan: <strong>{pe.challan_number}</strong></span>}
        {pe.created_at && <span>Created: <strong>{new Date(pe.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong></span>}
        {pe.notes && <span className="italic text-gray-500">Notes: {pe.notes}</span>}
        {pe.freight_charges > 0 && <span>Freight: <strong>₹{pe.freight_charges.toLocaleString('en-IN')}</strong></span>}
      </div>

      {/* Pivot Table */}
      <Card data-testid="pe-detail-items">
        <CardContent className="p-0">
          <DetailSearchSort search={searchDesign} setSearch={setSearchDesign} sortBy={sortBy} setSortBy={setSortBy} sortOptions={['amount']} testIdPrefix="pe-detail" />
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse" data-testid="pe-excel-table">
              <thead>
                <tr className="bg-[#1E1E23] text-white text-xs uppercase tracking-wider">
                  <th className="px-3 py-2.5 text-center font-medium border border-gray-700 w-10">SR</th>
                  <th className="px-2 py-2.5 text-center font-medium border border-gray-700 w-16">Image</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Design</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Brand</th>
                  <th className="px-3 py-2.5 text-left font-medium border border-gray-700">Colour</th>
                  {activeSizes.map(s => <th key={s} className="px-2 py-2.5 text-center font-medium border border-gray-700 min-w-[42px]">{s}</th>)}
                  <th className="px-3 py-2.5 text-center font-medium border border-gray-700">Qty</th>
                  <th className="px-3 py-2.5 text-right font-medium border border-gray-700">Amount</th>
                </tr>
              </thead>
              <tbody>
                {filterAndSortRows(rows, searchDesign, sortBy).map((r, ri) => {
                  const images = getProductImages(r.item.product_id);
                  const firstImg = images[0];
                  return (
                    <React.Fragment key={ri}>
                      <tr className="bg-white hover:bg-emerald-50/40 transition-colors">
                        <td className="px-3 py-2 text-center text-gray-500 border border-gray-500 font-mono text-xs" rowSpan="2">{r.sr}</td>
                        <td className="px-2 py-1.5 text-center border border-gray-500" rowSpan="2">
                          {firstImg ? <img src={`${API_BASE}${firstImg}`} alt={r.item.design_code} className="w-11 h-11 rounded object-cover cursor-pointer hover:ring-2 hover:ring-emerald-400 transition-all mx-auto" onClick={() => setLightboxImg(`${API_BASE}${firstImg}`)} /> : <div className="w-11 h-11 bg-gray-100 rounded flex items-center justify-center text-gray-400 mx-auto"><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>}
                        </td>
                        <td className="px-3 py-2 font-semibold text-gray-900 border border-gray-500" rowSpan="2">{r.item.design_code}</td>
                        <td className="px-3 py-2 text-gray-600 border border-gray-500 text-xs" rowSpan="2">{getBrandByProduct(r.item.product_id)}</td>
                        <td className="px-3 py-2 font-medium text-gray-800 border border-gray-500" rowSpan="2">{r.color}</td>
                        {activeSizes.map(s => {
                          const q = r.sizes[s] || 0;
                          return <td key={s} className="px-2 py-2 text-center border border-gray-500">{q > 0 ? <span className="font-medium">{q}</span> : <span className="text-gray-300">-</span>}</td>;
                        })}
                        <td className="px-3 py-2 text-center font-bold text-gray-900 border border-gray-500" rowSpan="2">{r.qty}</td>
                        <td className="px-3 py-2 text-right font-semibold text-gray-900 border border-gray-500" rowSpan="2">{r.amount > 0 ? `₹${r.amount.toLocaleString('en-IN')}` : ''}</td>
                      </tr>
                      <tr className="bg-white">
                        {(() => {
                          const cells = []; let sIdx = 0;
                          while (sIdx < activeSizes.length) {
                            const s = activeSizes[sIdx]; const q = r.sizes[s] || 0;
                            if (q === 0) { cells.push(<td key={sIdx} className="border border-gray-500"></td>); sIdx++; continue; }
                            const group = r.costGroups.find(g => g.sizes.includes(s));
                            if (group) { let span = 0; for (let i = sIdx; i < activeSizes.length; i++) { if (group.sizes.includes(activeSizes[i])) span++; else break; } cells.push(<td key={sIdx} colSpan={span} className="px-2 py-1.5 text-center border border-gray-500"><span className="font-semibold text-blue-800 text-xs">@₹{group.cost.toLocaleString('en-IN')}</span></td>); sIdx += span; }
                            else { cells.push(<td key={sIdx} className="border border-gray-500"></td>); sIdx++; }
                          }
                          return cells;
                        })()}
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-[#218C59] text-white font-bold text-sm">
                  <td className="px-3 py-2.5 border border-green-700" colSpan="5">TOTAL</td>
                  {activeSizes.map(s => { const t = rows.reduce((sum, r) => sum + (r.sizes[s] || 0), 0); return <td key={s} className="px-2 py-2.5 text-center border border-green-700">{t || ''}</td>; })}
                  <td className="px-3 py-2.5 text-center border border-green-700">{grandQty}</td>
                  <td className="px-3 py-2.5 text-right border border-green-700">₹{grandAmount.toLocaleString('en-IN')}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Lightbox */}
      <Dialog open={!!lightboxImg} onOpenChange={() => setLightboxImg(null)}>
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-none" aria-describedby={undefined}>
          <DialogTitle className="sr-only">Product Image</DialogTitle>
          <img src={lightboxImg} alt="Full size" className="w-full h-auto max-h-[85vh] object-contain rounded" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchaseEntryDetail;
